import {
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { map } from 'rxjs';
import { VxBadgeComponent } from '@ui/badge/badge.component';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxCardComponent } from '@ui/card/card.component';
import { VxCardFooterComponent } from '@ui/card/card-footer.component';
import { VxCardHeaderComponent } from '@ui/card/card-header.component';
import { VxDialogService } from '@ui/dialog/dialog.service';
import { VxIconComponent } from '@ui/icon/icon.component';
import { VxPanelComponent } from '@ui/panel/panel.component';
import { VxSkeletonComponent } from '@ui/skeleton/skeleton.component';
import { VxSwitchComponent } from '@ui/switch/switch.component';
import { VxTooltipDirective } from '@ui/tooltip/tooltip.directive';
import { SharedModule } from 'src/app/core/shared.module';
import { BreadcrumbService } from 'src/app/services/breadcrumb/breadcrumb.service';
import { CheckRoleDirective } from 'src/app/services/permission/permission.directive';
import { ToastService } from 'src/app/services/toast/toast.service';
import { TranslationService } from 'src/app/services/translation/translation.service';
import {
  DeleteAlertProcessorAction,
  LoadAlertProcessorAction,
  RunAlertProcessorSimulationAction,
  UpdateAlertProcessorAction,
} from 'src/app/store/alerts/alerts.actions';
import { AlertsState } from 'src/app/store/alerts/alerts.state';
import { UserState } from 'src/app/store/user/user.state';
import {
  buildAlertProcessorConfigurationForm,
  patchAlertProcessorConfigurationForm,
  toAlertProcessorConfigurationSpec,
} from '../components/configuration-wizard/configuration-form';
import { AlertConfigurationWizardComponent } from '../components/configuration-wizard/configuration-wizard.component';
import { AlertProcessorSimulationResultsDialogComponent } from './dialogs/simulation-results.component';
import { ConfirmService } from 'src/app/services/dialog/confirm.service';

const SIMULATION_ALERT_COUNT = 100;

@Component({
  selector: 'app-page-settings-alert-processor-details',
  templateUrl: 'details.component.html',
  imports: [
    SharedModule,
    ReactiveFormsModule,
    CheckRoleDirective,
    AlertConfigurationWizardComponent,
    VxBadgeComponent,
    VxButtonComponent,
    VxCardComponent,
    VxCardFooterComponent,
    VxCardHeaderComponent,
    VxIconComponent,
    VxPanelComponent,
    VxSkeletonComponent,
    VxSwitchComponent,
    VxTooltipDirective,
  ],
  host: { class: 'block' },
})
export class AlertProcessorDetailsSettingsPageComponent {
  private store = inject(Store);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private breadcrumbs = inject(BreadcrumbService);
  private translation = inject(TranslationService);
  private toast = inject(ToastService);
  private dialog = inject(VxDialogService);

  private readonly confirmService = inject(ConfirmService);
  protected configurationFG = buildAlertProcessorConfigurationForm();

  protected isSaving = signal(false);
  protected isSimulating = signal(false);

  private processorId = toSignal(
    this.route.paramMap.pipe(map(params => params.get('id')))
  );

  private processorEntity = this.store.selectSignal(
    AlertsState.selectedAlertProcessor
  );

  private hasPermission = this.store.selectSignal(
    UserState.checkPermissionRole(['alertprocessors.manager'])
  );

  protected processor = computed(() => this.processorEntity().data);

  protected isLoading = computed(
    () => !this.processor() && (this.processorEntity().isLoading ?? true)
  );

  protected deleteTooltip = computed(() => {
    this.translation.currentLocale$();
    return this.processor()?.isDefault
      ? this.translation.translateInstantKey(
          'pages.settings.alerting.processors.details.cannotDeleteDefaultProcessor'
        )
      : undefined;
  });

  constructor() {
    this.initLoading();
    this.initFormSync();
    this.initPermissionGate();
    this.initBreadcrumb();
  }

  private initLoading(): void {
    effect(() => {
      const id = this.processorId();
      if (!id) {
        return;
      }
      this.store.dispatch(new LoadAlertProcessorAction(id));
    });
  }

  /** Loads the stored configuration into the form whenever the entity arrives or is saved. */
  private initFormSync(): void {
    effect(() => {
      const processor = this.processor();
      if (!processor) {
        return;
      }
      untracked(() =>
        patchAlertProcessorConfigurationForm(
          this.configurationFG,
          processor.configuration
        )
      );
    });
  }

  private initPermissionGate(): void {
    effect(() => {
      if (this.hasPermission()) {
        this.configurationFG.enable();
      } else {
        this.configurationFG.disable();
      }
    });
  }

  private initBreadcrumb(): void {
    effect(() => {
      this.breadcrumbs.setPageTitle(this.route, this.processor()?.name || '');
    });
  }

  protected save(): void {
    const processor = this.processor();
    if (!processor || this.configurationFG.invalid) {
      this.configurationFG.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    this.store
      .dispatch(
        new UpdateAlertProcessorAction(processor.id, {
          name: processor.name,
          configuration: toAlertProcessorConfigurationSpec(
            this.configurationFG
          ),
        })
      )
      .subscribe({
        next: () => {
          this.configurationFG.markAsPristine();
          this.toast.showInfo(
            'pages.settings.alerting.processors.details.toasts.saved.header',
            'pages.settings.alerting.processors.details.toasts.saved.message'
          );
        },
        // the state already surfaces the error as a toast
        error: () => undefined,
      })
      .add(() => this.isSaving.set(false));
  }

  protected simulate(): void {
    const processor = this.processor();
    if (!processor) {
      return;
    }

    this.isSimulating.set(true);
    this.store
      .dispatch(
        new RunAlertProcessorSimulationAction(processor.id, {
          numberOfAlerts: SIMULATION_ALERT_COUNT,
        })
      )
      .subscribe({
        next: () =>
          this.dialog.open(AlertProcessorSimulationResultsDialogComponent),
        error: () => undefined,
      })
      .add(() => this.isSimulating.set(false));
  }

  protected deleteProcessor(): void {
    const processor = this.processor();
    if (!processor || processor.isDefault) {
      return;
    }

    const ns = 'pages.settings.alerting.processors.details.deletionDialog';
    this.confirmService
      .confirm({
        title: `${ns}.header`,
        description: `${ns}.description`,
        confirmLabel: `${ns}.confirm`,
        tone: 'danger',
        icon: 'trash',
      })
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }
        this.store
          .dispatch(new DeleteAlertProcessorAction(processor.id))
          .subscribe({
            next: () => this.router.navigate(['/settings/alerting']),
          });
      });
  }
}
